import { useMemo } from 'react';

/**
 * Hook to detect if the current device supports touch input.
 * Uses multiple detection methods for maximum reliability.
 *
 * @returns {boolean} True if the device supports touch input, false otherwise
 *
 * @example
 * ```tsx
 * const isTouch = useIsTouchDevice();
 * if (isTouch) {
 *   // Adjust UI for touch devices
 * }
 * ```
 */
export const useIsTouchDevice = (): boolean => {
  return useMemo(() => {
    // Check for touch support using multiple methods
    // 1. Modern standard: navigator.maxTouchPoints
    if (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0) {
      return true;
    }

    // 2. Legacy: ontouchstart event support
    if (typeof window !== 'undefined' && 'ontouchstart' in window) {
      return true;
    }

    // 3. CSS media query: pointer with coarse accuracy (touch)
    if (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(pointer: coarse)')?.matches
    ) {
      return true;
    }

    return false;
  }, []);
};
